import type { AutocompleteInput } from 'ui/autocomplete-input/autocomplete-input';

import { logger } from 'cadenza/utils/logging';
import type { LocationFinderAutocompleteItemData } from 'cadenza/map/location-finder/location-finder';
import type { LocationFinderDataProviderDto } from 'cadenza/map/location-finder/location-finder-api-client';

export interface LocationFinderSearchConfiguration {
  useCurrentBBox: boolean;
  dataProviders: LocationFinderDataProviderDto[];
}
/**
 * Creates a locationFinderDropdown underneath the button.
 *
 * @param buttonResponsibleForOpeningDropdown - We are passing the button that is responsible
 * for opening dropdown because of following reasons:
 * 1. It is used to calculate where to show the dropdown (under the button).
 * 2. For setting the correct attribute value for 'aria-expanded'.
 * @param locationFinderAutocomplete - The autocomplete is necessary so that we can refresh the
 *     autocomplete suggestions when the configuration changes
 * @param locationFinderSearchConfiguration - LocationFinderSearchConfiguration that will be used for this dropdown.
 * @param options - Additional options.
 * @param [options.onChange] - Callback that is executed when the Location Finder config was changed.
 * @param [options.onClose] - Callback that is executed when the dropdown is closed. Use it to update the settings.
 * @param [options.showHeader] - Boolean that says whether we want to show additional heading 'Search for:'
 */
export function createLocationFinderDropdown (
  buttonResponsibleForOpeningDropdown: HTMLButtonElement, // Needed so that we can calculate the position of the dropdown.
  locationFinderAutocomplete: AutocompleteInput<LocationFinderAutocompleteItemData> | undefined,
  locationFinderSearchConfiguration: LocationFinderSearchConfiguration,
  {
    onChange = () => { /* noop */ },
    onClose =  () => { /* noop */ },
    showHeader = true
  }: {
    onChange?: (configuration: LocationFinderSearchConfiguration) => void;
    onClose?: (configuration: LocationFinderSearchConfiguration) => void;
    showHeader?: boolean;
  } = {}
) {
  import(/* webpackChunkName: "location-finder-providers-dropdown" */ 'cadenza/map/location-finder/location-finder-providers-dropdown')
    .then(({ LocationFinderProvidersDropdown }) => {
      if (locationFinderSearchConfiguration.dataProviders.length === 0) {
        logger.warn('No location finder suggestion providers are available.');
        return;
      }
      buttonResponsibleForOpeningDropdown.setAttribute('aria-expanded', 'true');
      const locationFinderProvidersDropdown = new LocationFinderProvidersDropdown(
        locationFinderSearchConfiguration,
        {
          withHeading: showHeader,
          onClose: () => {
            // aria-expanded set to false in timeout to prevent popup to be reopened after clicking on button
            setTimeout(() => buttonResponsibleForOpeningDropdown.setAttribute('aria-expanded', 'false'), 200);
            if (locationFinderAutocomplete && (locationFinderProvidersDropdown.selectedProvidersChanged || locationFinderProvidersDropdown.useCurrentBBoxChanged)) {
              locationFinderAutocomplete.refreshSuggestions();
            }
            onClose({
              useCurrentBBox: locationFinderProvidersDropdown.useCurrentBBox,
              dataProviders: locationFinderSearchConfiguration.dataProviders
                .map(provider => {
                  return {
                    id: provider.id,
                    label: provider.label,
                    listItemHeadline: provider.listItemHeadline,
                    checked: locationFinderProvidersDropdown.selectedProviders.some(selected => selected === provider.id),
                    maxZoomScale: provider.maxZoomScale
                  };
                })
            });
          },
          onChange
        });

      // Show dropdown.
      locationFinderProvidersDropdown.show(buttonResponsibleForOpeningDropdown);
    })
    .catch((error) => {
      logger.error('Failed to show providers dropdown', error);
    });
}

export function isLocationFinderSearchRestricted ({ useCurrentBBox, dataProviders }: LocationFinderSearchConfiguration) {
  return useCurrentBBox // Search only in current extent
    // Not all active (Note: none checked == all checked)
    || (dataProviders.some(({ checked }) => checked) && dataProviders.some(({ checked }) => !checked));
}
